import { QTY_LIMITS, isFractionalItem, qtyMin } from '../util/quantity';
import { Util } from '../util/Util';

import { roundQty } from "../util/quantity";
type StockRow = { id: number; quantity: number; uniqueId: string; isFractional?: boolean };

export const isItemMetered = (catalogItem: any, defaultMetered = true): boolean => {
  if (catalogItem == null) {
    return defaultMetered;
  }

  const raw = catalogItem.IsMetered;
  if (raw === undefined || raw === null) {
    return defaultMetered;
  }
  if (typeof raw === 'boolean') {
    return raw;
  }
  if (typeof raw === 'number') {
    return raw !== 0;
  }

  return Util.isBoolSettingEnabled(String(raw), defaultMetered);
};

export const buildItemCatalogMap = (items: any[]) => {
  const map = new Map<number, any>();
  for (const item of items) {
    map.set(item.id, item);
  }
  return map;
};

export const buildTaskQuantityByItemId = <T extends StockRow>(rows: readonly T[] | undefined) => {
  const map = new Map<number, number>();
  for (const row of rows ?? []) {
    map.set(row.id, (map.get(row.id) ?? 0) + row.quantity);
  }
  return map;
};

export const buildBundleQuantityByItemId = (bundle: any[] | undefined) => {
  const map = new Map<number, number>();
  for (const bundleItem of bundle ?? []) {
    const itemId = parseInt(bundleItem.item_id, 10);
    if (!Number.isFinite(itemId)) continue;
    map.set(itemId, (map.get(itemId) ?? 0) + bundleItem.quantity);
  }
  return map;
};

export const isNegativeStockAllowed = (settingValue: string | undefined) =>
  Util.isBoolSettingEnabled(settingValue);

/**
 * Stock caps apply only for metered items when stock checking is on and
 * ALLOW_NEGATIVE_STOCK is disabled. Unmetered items and supply (checkStock=false)
 * never enforce stock limits.
 */
export const shouldEnforceStockQuantity = ({
  isMetered = true,
  checkStock = true,
  allowNegativeStock = false,
}: {
  isMetered?: boolean;
  checkStock?: boolean;
  allowNegativeStock?: boolean;
}) => isMetered && checkStock && !allowNegativeStock;

export const buildQuantityMaxByRowId = <T extends StockRow>({
  rows,
  itemsById,
  taskQtyByItemId,
  bundleQtyByItemId,
  checkStock,
  allowNegativeStock,
}: {
  rows: readonly T[] | undefined;
  itemsById: Map<number, any>;
  taskQtyByItemId: Map<number, number>;
  bundleQtyByItemId: Map<number, number>;
  checkStock: boolean;
  allowNegativeStock: boolean;
}) => {
  const map = new Map<string, number>();
  const maxLimit = QTY_LIMITS.max;

  for (const row of rows ?? []) {
    const catalogItem = itemsById.get(row.id);
    const isMetered = isItemMetered(catalogItem, true);

    if (!shouldEnforceStockQuantity({ isMetered, checkStock, allowNegativeStock })) {
      map.set(row.uniqueId, maxLimit);
      continue;
    }

    const bundleQty = bundleQtyByItemId.get(row.id) ?? 0;
    const taskQty = taskQtyByItemId.get(row.id) ?? 0;
    const activeStock = roundQty((catalogItem?.Stock ?? 0) + bundleQty + row.quantity - taskQty);
    const fractional = isFractionalItem(row) || isFractionalItem(catalogItem);

    map.set(
      row.uniqueId,
      Math.min(maxLimit, Math.max(activeStock, qtyMin(fractional))),
    );
  }

  return map;
};